import { useEffect, ChangeEvent } from 'react';
import {
    Stack,
    Box,
    Typography,
    FormControlLabel,
    Switch,
    TextField,
    Divider,
    Chip,
} from '@mui/material';
import { useI18nContext } from '@/i18n/i18n-react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { updateFormData, setStepErrors } from '@/store/installSlice';

export const MailStep = () => {
    const { LL } = useI18nContext();
    const dispatch = useAppDispatch();
    const formData = useAppSelector((state) => state.install.formData);

    // Validate this step
    useEffect(() => {
        const errors: string[] = [];
        if (formData.mailEnabled) {
            if (!formData.mailHost.trim()) {
                errors.push(LL.install.mail.errors.hostRequired());
            }
            const port = parseInt(formData.mailPort, 10);
            if (!formData.mailPort || isNaN(port) || port <= 0) {
                errors.push(LL.install.mail.errors.portRequired());
            }
            if (!formData.mailUser.trim()) {
                errors.push(LL.install.mail.errors.usernameRequired());
            }
            if (!formData.mailPassword.trim()) {
                errors.push(LL.install.mail.errors.passwordRequired());
            }
            if (!formData.mailSender.trim()) {
                errors.push(LL.install.mail.errors.senderRequired());
            }
        }

        dispatch(setStepErrors({ step: 2, errors }));
    }, [
        formData.mailEnabled,
        formData.mailHost,
        formData.mailPort,
        formData.mailUser,
        formData.mailPassword,
        formData.mailSender,
        dispatch,
        LL,
    ]);

    const handleChange = (field: string) => (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        dispatch(updateFormData({ [field]: value }));
    };

    return (
        <Stack spacing={3}>
            <Box>
                <Typography
                    variant="body2"
                    sx={{
                        color: 'text.secondary',
                        mb: 2,
                    }}
                >
                    {LL.install.mail.description()}
                </Typography>
                <FormControlLabel
                    control={
                        <Switch
                            checked={formData.mailEnabled}
                            onChange={handleChange('mailEnabled')}
                        />
                    }
                    label={LL.install.mail.enable()}
                />
            </Box>
            {formData.mailEnabled && (
                <>
                    <TextField
                        label={LL.install.mail.host()}
                        value={formData.mailHost}
                        onChange={handleChange('mailHost')}
                        fullWidth
                    />

                    <TextField
                        label={LL.install.mail.port()}
                        type="number"
                        value={formData.mailPort}
                        onChange={handleChange('mailPort')}
                        fullWidth
                    />

                    <TextField
                        label={LL.install.mail.username()}
                        placeholder={LL.install.mail.usernamePlaceholder()}
                        value={formData.mailUser}
                        onChange={handleChange('mailUser')}
                        fullWidth
                    />

                    <TextField
                        label={LL.install.mail.password()}
                        type="password"
                        value={formData.mailPassword}
                        onChange={handleChange('mailPassword')}
                        fullWidth
                    />

                    <Divider>
                        <Chip label={LL.install.mail.emailHeaders()} />
                    </Divider>

                    <TextField
                        label={LL.install.mail.sender()}
                        placeholder={LL.install.mail.senderPlaceholder()}
                        value={formData.mailSender}
                        onChange={handleChange('mailSender')}
                        fullWidth
                    />

                    <TextField
                        label={LL.install.mail.replyTo()}
                        placeholder={LL.install.mail.replyToPlaceholder()}
                        value={formData.mailReplyTo}
                        onChange={handleChange('mailReplyTo')}
                        fullWidth
                    />
                </>
            )}
        </Stack>
    );
};
